/**
 * Record lists — the admin half of @vv/sections' RECORD_SCHEMAS / LIST_META.
 *
 * The Pages editor's Lists mode is generic; everything list-specific lives
 * here: the API route + response keys, how a record reads in the rail,
 * per-field form meta (labels, hints, widget overrides), the POST body for
 * a new record, and the DISPLAY values the live preview patches into the
 * site's [data-rf] leaves. Those display values mirror the Astro sections'
 * formatting (apps/site/src/page-builder/sections/*.astro) — keep the two
 * in step, or the preview shows text the published site won't.
 */

import {
  DRIP_CATEGORIES,
  DRIP_MENU_PAGE_CATEGORIES,
  LIST_META,
  RECORD_SCHEMAS,
  type ListKey,
} from '@vv/sections';
import type { FieldMeta } from './formgen';

/** A record as the admin API returns it (booleans already converted). */
export type ListRecord = Record<string, unknown> & {
  id: string;
  display_order: number;
  is_active: boolean;
};

export type PreviewValue = string | number | string[];

export interface ListDef {
  key: ListKey;
  /** Admin API collection path. */
  apiPath: string;
  /** GET response key holding the array ({ items: [...] }). */
  listProp: string;
  /** POST/PATCH response key holding the row ({ item: {...} }). */
  itemProp: string;
  /** Record field that gets " (copy)" on duplicate. */
  titleField: string;
  /** Rail row line 1 / line 2 / small chip. */
  rowTitle: (r: ListRecord) => string;
  rowSub: (r: ListRecord) => string;
  rowChip?: (r: ListRecord) => string | null;
  /** False = this record never renders on a website page (booking-only
   * drips) — the rail says so instead of pretending it will preview. */
  onPage?: (r: ListRecord) => boolean;
  offPageNote?: string;
  fieldMeta: Record<string, FieldMeta>;
  /** POST body for a new record in `group` (grouped lists). */
  createBody: (group: string | undefined, siblings: ListRecord[]) => Record<string, unknown>;
  /** Keys the create endpoint accepts — duplicate POSTs these, then
   * autosaves the rest as a normal draft. */
  createKeys: string[];
  /** [data-rf] display values for the live preview. */
  previewFields: (r: ListRecord) => Record<string, PreviewValue>;
  /** [data-rf] names that aren't form fields → the field a click focuses. */
  previewFieldFor?: Record<string, string>;
  /** Fields the live preview can't show at all (styling, links, badges,
   * photos without a [data-rf] leaf) — edits say "after the next Publish". */
  publishOnlyFields?: string[];
  /** Fields whose [data-rf] leaf only exists when the value was set at
   * build time (conditional markup) — an empty→filled edit can't patch. */
  conditionalFields?: string[];
  /** Preview page when no page doc references the list (dev hasn't
   * placed its section anywhere yet). */
  fallbackHref: string;
}

const clip = (s: unknown, n = 60): string => {
  const text = typeof s === 'string' ? s.replace(/<[^>]+>/g, '').trim() : '';
  return text.length > n ? `${text.slice(0, n)}…` : text;
};

const dollars = (cents: unknown): string =>
  typeof cents === 'number' ? `$${Math.round(cents / 100)}` : '$0';

const prettyCategory = (c: unknown): string => {
  const s = String(c ?? '');
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

/** Group select meta for page-grouped lists — ListWorkspace fills the
 * options per render from the groups that exist (records + sections). */
export function groupFieldMeta(
  options: Array<{ value: string; label: string }>,
  itemLabel: string,
): FieldMeta {
  return {
    label: 'Group',
    widget: 'select',
    options,
    hint: `Which page's ${itemLabel} group this belongs to. Each ${itemLabel === 'step' ? 'Steps' : 'FAQ'} section shows one group.`,
  };
}

export const LIST_DEFS: Record<ListKey, ListDef> = {
  drips: {
    key: 'drips',
    apiPath: '/api/admin/drip-menu',
    listProp: 'items',
    itemProp: 'item',
    titleField: 'name',
    rowTitle: (r) => String(r.name ?? 'Untitled drip'),
    rowSub: (r) => [dollars(r.price_cents), r.duration_minutes ? `${r.duration_minutes} min` : '']
      .filter(Boolean)
      .join(' · '),
    rowChip: (r) => prettyCategory(r.category),
    onPage: (r) => (DRIP_MENU_PAGE_CATEGORIES as readonly string[]).includes(String(r.category)),
    offPageNote: 'Booking only — shots and the GLP-1 program show in the booking widget, not on the Services page.',
    fieldMeta: {
      name: { label: 'Name' },
      category: {
        label: 'Category',
        options: DRIP_CATEGORIES.map((c) => ({
          value: c,
          label:
            c === 'shots' || c === 'program'
              ? `${prettyCategory(c)} (booking only)`
              : prettyCategory(c),
        })),
        hint: 'Hydration, Performance, Beauty and Wellness drips show on the Services page.',
      },
      price_cents: { label: 'Price', hint: 'Shown rounded to whole dollars on the card.' },
      duration_minutes: { label: 'Length (minutes)', placeholder: 'e.g. 45' },
      description: { label: 'Description', widget: 'textarea' },
      tag: { label: 'Card footnote', hint: 'Small italic line at the bottom of the card, e.g. "Hydration only · entry tier".' },
      is_popular: { label: '“Most popular” badge' },
      image_url: { label: 'Photo', aspect: '16/10', hint: 'Optional — the card shows without a photo too.' },
    },
    createBody: () => ({ name: 'New drip', category: 'hydration', price_cents: 0 }),
    createKeys: ['name', 'category', 'description', 'price_cents', 'duration_minutes', 'tag'],
    previewFields: (r) => ({
      name: String(r.name ?? ''),
      price: dollars(r.price_cents),
      description: String(r.description ?? ''),
      tag: String(r.tag ?? ''),
    }),
    previewFieldFor: { price: 'price_cents' },
    publishOnlyFields: ['category', 'is_popular'],
    conditionalFields: ['image_url'],
    fallbackHref: '/services',
  },

  services: {
    key: 'services',
    apiPath: '/api/admin/marketing-services',
    listProp: 'services',
    itemProp: 'service',
    titleField: 'title',
    rowTitle: (r) => String(r.short_title || r.title || 'Untitled service'),
    rowSub: (r) => [r.price_label, r.price_unit].filter(Boolean).join(' '),
    rowChip: (r) => (r.is_program ? 'Program' : null),
    fieldMeta: {
      title: { label: 'Title (Services page)' },
      subtitle: { label: 'Subtitle (Services page)', widget: 'text' },
      short_title: { label: 'Short title (home card)' },
      card_blurb: { label: 'Card blurb (home card)', widget: 'textarea' },
      icon: { label: 'Icon' },
      image_url: { label: 'Card photo (home card)', aspect: '4/3' },
      best_for: { label: 'Best for', hint: 'One line per bullet.' },
      includes: { label: 'What’s included', hint: 'One line per bullet.' },
      price_label: { label: 'Price', hint: 'As written, e.g. "$100 – $225". The home card shows "from" the first amount.' },
      price_unit: { label: 'Price unit', hint: 'e.g. "per infusion" or "per month".' },
      tag: { label: 'Tag', hint: '"Most popular" also highlights the Services page row.' },
      is_program: { label: 'Program (dark row + program button)' },
      link_to: { label: 'Button link (optional)', placeholder: '/glp1', hint: 'Empty = the row’s button books a consult.' },
    },
    createBody: () => ({
      title: 'New service',
      short_title: 'New service',
      icon: 'drop',
      price_label: '$0',
      price_unit: 'per visit',
    }),
    createKeys: ['title', 'short_title', 'icon', 'price_label', 'price_unit'],
    // Two renders: detail rows (/services) and cards (home). card_price
    // mirrors ServiceCards.astro: `from <first amount>[/mo]`.
    previewFields: (r) => {
      const label = String(r.price_label ?? '');
      const unit = String(r.price_unit ?? '');
      return {
        title: String(r.title ?? ''),
        subtitle: String(r.subtitle ?? ''),
        best_for: Array.isArray(r.best_for) ? r.best_for.map(String) : [],
        includes: Array.isArray(r.includes) ? r.includes.map(String) : [],
        price_label: label,
        price_unit: unit,
        short_title: String(r.short_title ?? ''),
        card_blurb: String(r.card_blurb ?? ''),
        card_price: `from ${label.split('–')[0]?.trim() ?? label}${unit.includes('month') ? '/mo' : ''}`,
        tag: String(r.tag ?? ''),
      };
    },
    previewFieldFor: { card_price: 'price_label' },
    publishOnlyFields: ['icon', 'image_url', 'is_program', 'link_to'],
    conditionalFields: ['tag', 'best_for', 'includes'],
    fallbackHref: '/services',
  },

  faqs: {
    key: 'faqs',
    apiPath: '/api/admin/faqs',
    listProp: 'faqs',
    itemProp: 'faq',
    titleField: 'question',
    rowTitle: (r) => clip(r.question, 80) || 'Untitled question',
    rowSub: (r) => clip(r.answer),
    fieldMeta: {
      question: { label: 'Question' },
      answer: { label: 'Answer', widget: 'textarea' },
    },
    createBody: (group) => ({
      page: group ?? 'services',
      question: 'New question?',
      answer: 'The answer.',
      is_active: false,
    }),
    createKeys: ['page', 'question', 'answer'],
    previewFields: (r) => ({
      question: String(r.question ?? ''),
      answer: String(r.answer ?? ''),
    }),
    publishOnlyFields: ['page'],
    fallbackHref: '/services',
  },

  steps: {
    key: 'steps',
    apiPath: '/api/admin/program-steps',
    listProp: 'steps',
    itemProp: 'step',
    titleField: 'title',
    rowTitle: (r) => `${String(r.step_number ?? '')} · ${String(r.title ?? '')}`,
    rowSub: (r) => clip(r.body),
    fieldMeta: {
      step_number: { label: 'Number label', placeholder: '01', hint: 'Shown big beside the step, e.g. "01".' },
      title: { label: 'Title' },
      body: { label: 'Text', widget: 'textarea' },
    },
    createBody: (group, siblings) => ({
      page: group ?? 'glp1',
      step_number: String(siblings.length + 1).padStart(2, '0'),
      title: 'New step',
      body: 'What happens in this step.',
      is_active: false,
    }),
    createKeys: ['page', 'step_number', 'title', 'body'],
    previewFields: (r) => ({
      step_number: String(r.step_number ?? ''),
      title: String(r.title ?? ''),
      body: String(r.body ?? ''),
    }),
    publishOnlyFields: ['page'],
    fallbackHref: '/glp1',
  },

  testimonials: {
    key: 'testimonials',
    apiPath: '/api/admin/testimonials',
    listProp: 'testimonials',
    itemProp: 'testimonial',
    titleField: 'attribution',
    rowTitle: (r) =>
      [r.attribution, r.location].filter((v) => typeof v === 'string' && v).join(' · ') ||
      'Unnamed',
    rowSub: (r) => clip(r.quote),
    fieldMeta: {
      quote: { label: 'Quote', widget: 'textarea', hint: 'Without quote marks — the card adds them.' },
      attribution: { label: 'Name', hint: 'First name + last initial, e.g. "Megan R."' },
      location: { label: 'Town', placeholder: 'Wasilla' },
      context: { label: 'Service', placeholder: 'Myers + Immunity', hint: 'Small line on the right of the card.' },
    },
    createBody: () => ({ quote: 'Quote text.', attribution: 'First name + initial' }),
    createKeys: ['quote', 'attribution', 'location', 'context'],
    previewFields: (r) => ({
      quote: String(r.quote ?? ''),
      attribution: String(r.attribution ?? ''),
      location: String(r.location ?? ''),
      context: String(r.context ?? ''),
    }),
    conditionalFields: ['location'],
    fallbackHref: '/',
  },
};

/** Editable keys of a list, in form order (RECORD_SCHEMAS shape order). */
export function editableKeys(list: ListKey): string[] {
  return Object.keys(RECORD_SCHEMAS[list].shape);
}

/** Canonical JSON of a record's editable fields — the dirty-check unit.
 * Absent and null compare equal (the form clears optionals to absent,
 * the API stores null). */
export function editableJson(list: ListKey, r: Record<string, unknown>): string {
  const out: Record<string, unknown> = {};
  for (const k of editableKeys(list)) out[k] = r[k] ?? null;
  return JSON.stringify(out);
}

export function isGrouped(list: ListKey): boolean {
  return LIST_META[list].groupBy === 'page';
}

/** Group key of a record ('' for flat lists). */
export function groupOf(list: ListKey, r: Record<string, unknown>): string {
  return isGrouped(list) ? String(r.page ?? '') : '';
}

/** `[data-rec]` value of a record in the site's DOM. */
export function recKey(list: ListKey, id: string): string {
  return `${list}:${id}`;
}
